import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Vue } from 'vue-property-decorator';
import axios from 'axios';
import TMainDashboard from '@/types/Dashboard/MainDashboard/MainDashboard';
import Dictionary from '@/types/Dictionary';
import { API_URL } from '@/App.settings';
import SaveModeEnum from '@/enums/SaveModeEnum';
import MainWidget from '@/types/Dashboard/MainDashboard/MainWidget';

/** VueX de gestion des tableaux de bord principaux (MainDashboard) */
@Module({
	namespaced: true
})
class MainDashboardStore extends VuexModule {
//#region Data
	/** Liste des tableau de bord principaux */
	private mainDashboards: TMainDashboard[] = undefined;
	
	/** Mide de sauvegarde en cours */
	private saveMode: SaveModeEnum = SaveModeEnum.NewObject;

	/** Incrément temporaire d'Id de widget avant enregistrement */
	private tempObjectId = 0;
//#endregion Data

//#region Mutations
	/** Réinitialisation du store VueX */
	@Mutation
	private reset(){
		this.mainDashboards = undefined;
		this.saveMode = SaveModeEnum.NewObject;
		this.tempObjectId = 0;
	}

	/** 
	 * Affecte la liste des tableaux de bord complète
	 * @param mainDashboards Tableau de liste de tableaux de bord principaux
	 **/
	@Mutation
	private setMainDashboards(mainDashboards: TMainDashboard[]) {
		this.mainDashboards = mainDashboards;
	}

	/** 
	 * Ajout d'un tableau de bord principal dans la liste des tableaux de bord principaux disponibles
	 * @param mainDashboard Tableau de bord principal à ajouter
	 **/
	@Mutation
	private setMainDashboard(mainDashboard: TMainDashboard) {
		let index: number = this.mainDashboards.findIndex(x => x.Id == mainDashboard.Id)
		if (index > -1) {
			//this.mainDashboards[index] = mainDashboard;
			Vue.set(this.mainDashboards, index, mainDashboard);
			/** @hack Pour forcer la mutation sur les widgets à se diffuser dans les Prop de components... */
			Vue.set(this.mainDashboards[index], 'Widgets', mainDashboard.Widgets);
		}
		else {
			Vue.set(this.mainDashboards, this.mainDashboards.length, mainDashboard);
		}
	}

	/** Suppression d'un tableau de bord */
	@Mutation
	private delete(mainDashboardId: number) {
		let index: number = this.mainDashboards.findIndex(x => x.Id == mainDashboardId);
		if (index > -1)
			this.mainDashboards.splice(index, 1);
	}

	/** Applique un mode de sauvegarde du dashboard */
	@Mutation
	private setSaveMode({ saveMode }) {
		this.saveMode = saveMode;
	}

	/**  
	 * Met à jour une ou plusieurs propriétés d'un widget
	 * @param dashboardId Identifiant du tableau de bord principal
	 * @param widgetId Identifiant du widget à mettre à jour
	 * @param datas Ensemble de propriété/valeur à mettre à jour 
	 ***/
	@Mutation
	private updateWidget({ dashboardId, widgetId, datas }) {
		let widget: MainWidget = getWidget(dashboardId, 'Id', widgetId, this.mainDashboards)

		if (widget) {
			// Pour chaque paire clé-valeur, on met à jour la propriété sur le widget
			for(let key in datas) {
				/**  Vue.set(widget, 'State', state); */
				Vue.set(widget, key, datas[key]);
			}
		}
	}

	/** Met à jour le mode d'edition d'un tableau de bord */
	@Mutation
	private toggleEditMode(dashboardId) {
		let index: number = this.mainDashboards.findIndex(x => x.Id == dashboardId);
		if (index > -1) 
			Vue.set(this.mainDashboards[index], 'EditMode', !(this.mainDashboards[index].EditMode));
	}

	/** Décrémente le compteur d'objet temporaire */
    @Mutation
    private setTempObjectId() {
        --this.tempObjectId;
    }
//#endregion Mutations
//#region Actions
	/** Réinitialisation du store VueX */
	@Action({ rawError: true })
	public Reset(){
		this.context.commit('reset');
	}

	/** Charge la liste des tableaux de bord principaux */
	@Action({ rawError: true })
	public async LoadData() {
		const args: Dictionary<any> = {};
		args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

		await axios.post(API_URL + "MainDashboard/GetDashboards", args).then((response) => {
			if (response && response.data) {
				this.context.commit('setMainDashboards', response.data);
			}
		});
	}

	/** 
	 * Enregistrement du dashboard
	 * @param dashboard Objet tableau de bord à sauvegarder
	 * @param saveMode Mode de Sauvegarde (nouveau ou mise à jour)
	 **/
	@Action({ rawError: true })
	public async Save({ dashboard, saveMode = SaveModeEnum.UpdateObject }) {
		const args: Dictionary<any> = {};
		args["Token"] = Vue.cookies.get('access_token') != null ? Vue.cookies.get('access_token') : "";
		args["MainDashboard"] = JSON.stringify(dashboard);
		args["SaveMode"] = saveMode;

		return new Promise(async (resolve, reject) => {
			/** Stockage mode édition (qui n'est pas géré côté server) */
			let editMode: boolean = dashboard.EditMode;

			// Sauvegarde des dashboards existants
			await axios.post<TMainDashboard>(API_URL + "MainDashboard/Save", args).then((response) => {
				if (response && response.data) {

					console.debug("MainDashboardStore - Save -  response.data : %O", response.data);

					let dashboard: TMainDashboard = response.data;
					dashboard.EditMode = editMode;

					this.context.commit('setMainDashboard', dashboard) ;
				}
					
				resolve(response);
			}, (error) => {
				reject(error)})
			});
	}

	/** 
	 * Suppression d'un tableau de bord
	 * @param mainDashboardId Id de tableau de bord
	 */
	@Action({ rawError: true })
	public async Delete(mainDashboardId: number) {
		const args: Dictionary<any> = {};
		args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
		args["MainDashboardId"] = mainDashboardId;

		return new Promise(async (resolve, reject) => {
			await axios.post(API_URL + "MainDashboard/Delete", args).then((response) => {
				if (response) {
					// Suppression du tableau de bord de la liste
					this.context.commit('delete', mainDashboardId);

					/** @todo : supprimer uniquement le tableau de bord supprimé ou recharger toutes la liste des dashboards ? */
					resolve(response);
				}
			}, (error) => {
				console.error(error);
				reject(error)})
			});
	}

	/** Mise à jour des paramètres d'un widget sur un tableau de bord */
	@Action({ rawError: true })
	public UpdateWidgetParameters({ dashboardId, widgetId, name = undefined, parameters }) {
		console.debug("MainDashboardStore - UpdateWidgetParameters() : parameters : ", parameters);

		let datas = { Parameters: parameters };
		if (name)
			datas['Name'] = name;

		this.context.commit('updateWidget', { dashboardId, widgetId, datas: datas }) ;
	}

	/** 
	 * Mise à jour d'un widget sur un tableau de bord
	 * @param dashboardId Id du tableau de bord
	 * @param widgetId	Identifiant du widget
	 * @param datas Données du widget à mettre à jour
	 **/
	@Action({ rawError: true })
	public UpdateWidget({ dashboardId, widgetId, datas }) {
		console.debug("MainDashboardStore - UpdateWidget() : state : ", datas);

		this.context.commit('updateWidget', { dashboardId, widgetId, datas }) ;
	}

	@Action({ rawError: true })
	public ToggleEditMode(dashboardId) {
		this.context.commit('toggleEditMode', dashboardId) ;
	}

	@Action({ rawError: true })
    public async SetTempObjectId(tempWidgetId: number): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this.context.commit('setTempObjectId');
            resolve(true);
        });
    }
//#endregion Actions
//#region Getters
	public get GetMainDashboards(): TMainDashboard[] {
		return this.mainDashboards;
	}

	/** Renvoi le dernier WidgetId temporaire disponible */
    public get GetTempObjectId(): number {
        return this.tempObjectId;
    }
//#endregion Getters
}

export default MainDashboardStore;

//#region Fonctions locales
/** 
 * Renvoi l'index dans le tableau de widgets d'un dashboard
 * @param dashboardId Id du tableau de bord à rechercher
 * @param widgetProperty Propriété de widget dans laquelle rechercher
 * @param value Valeur à rechercher
 * @param mainDashboards Liste des tableaux de bord principaux
 */
function getWidget(dashboardId: number, widgetProperty: string, value: any, mainDashboards: TMainDashboard[]): MainWidget {
	let index: number = mainDashboards.findIndex(x => x.Id == dashboardId);
	if (index > -1) {
		let indexWidget: number = mainDashboards[index].Widgets.findIndex(x => x[widgetProperty] == value);
		if (indexWidget > -1)
			return mainDashboards[index].Widgets[indexWidget];
	}
	return undefined;
}
//#endregion Fonctions locales